import { useState, useMemo, useCallback, useEffect } from "react";
import * as React from "react";
import { ColDef, ICellRendererParams, GridOptions } from "ag-grid-community";
import AgGridTable from "../../components/table/AgGridTable";
import cveBugError from "../../assets/cve-bug-error.svg";
import cveBug from "../../assets/cve-bug.svg";
import {
  Card,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
  Button,
  Divider,
  FormControlLabel,
} from "@mui/material";
import IOSSwitch from "../../components/IOSSwitch";
import WhiteTooltip from "../../components/WhiteTooltip";
import { useTheme } from "@mui/material/styles";
import { DragIndicator, KeyboardArrowDown } from "@mui/icons-material";
import SoftwareDetailsDrawer from "./SoftwareDetailsDrawer";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { useStorageManager } from "../../shared/hooks";
// 🚀 Global variable to store vendors for AG Grid filter
let globalVendors: string[] = [];

// 🚀 Custom Vendor Filter Component for AG Grid
const VendorFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [availableVendors, setAvailableVendors] =
    useState<string[]>(globalVendors);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  // Filter vendors based on search text
  const filteredVendors = React.useMemo(() => {
    if (!searchText.trim()) return availableVendors;
    return availableVendors.filter((vendor) =>
      vendor.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [availableVendors, searchText]);

  React.useEffect(() => {
    setAvailableVendors([...globalVendors]);
  }, []);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => selectedVendors.length > 0 && !isAllSelected,
    doesFilterPass: (params: any) => {
      if (selectedVendors.length === 0 || isAllSelected) return true;
      return selectedVendors.includes(params.data.vendor);
    },
    getModel: () =>
      selectedVendors.length > 0 && !isAllSelected
        ? { selectedTypes: selectedVendors }
        : null,
    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedVendors(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedVendors([]);
        setIsAllSelected(true);
      }
    },
    afterGuiAttached: () => setAvailableVendors([...globalVendors]),
    getGui: () => null,
  }));

  const handleVendorToggle = (vendor: string) => {
    if (vendor === "All") {
      setIsAllSelected(true);
      setSelectedVendors([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedVendors.includes(vendor)
        ? selectedVendors.filter((v) => v !== vendor)
        : [...selectedVendors, vendor];
      setSelectedVendors(newSelected);
    }

    // Trigger AG Grid filter change
    setTimeout(() => {
      if (params.filterChangedCallback) {
        params.filterChangedCallback();
      }
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleApply = () => {
    window.dispatchEvent(
      new CustomEvent("vendorFilterChanged", {
        detail: { selectedTypes: isAllSelected ? [] : selectedVendors },
      }),
    );
    params.api?.hidePopupMenu?.();
  };

  const handleClear = () => {
    setSelectedVendors([]);
    setIsAllSelected(true);
    setSearchText("");
    window.dispatchEvent(
      new CustomEvent("vendorFilterChanged", {
        detail: { selectedTypes: [] },
      }),
    );
    if (params.filterChangedCallback) {
      params.filterChangedCallback();
    }
    params.api?.hidePopupMenu?.();
  };

  const isDarkMode = params.isDarkMode || false;

  return (
    <div
      style={{
        padding: "12px",
        minWidth: "240px",
        backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
        border: `1px solid ${isDarkMode ? "#2f2f37" : "#E4E7EC"}`,
        borderRadius: "8px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        fontSize: "13px",
        color: isDarkMode ? "#E5E7EB" : "#344054",
        fontFamily: "inherit",
      }}
    >
      <div style={{ marginBottom: "12px", fontWeight: 600, fontSize: "14px" }}>
        Filter Vendors
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: "12px" }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: `1px solid ${isDarkMode ? "#2f2f37" : "#D0D5DD"}`,
            borderRadius: "6px",
            backgroundColor: isDarkMode ? "#111111" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
            fontSize: "13px",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      <div
        style={{ maxHeight: "200px", overflowY: "auto", marginBottom: "12px" }}
      >
        {/* All option */}
        <div
          onClick={() => handleVendorToggle("All")}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "6px 8px",
            cursor: "pointer",
            backgroundColor: isAllSelected
              ? isDarkMode
                ? "rgba(255,255,255,0.05)"
                : "#F9FAFB"
              : "transparent",
            borderRadius: "4px",
            marginBottom: "2px",
          }}
        >
          <Checkbox size="small" checked={isAllSelected} sx={{ p: 0, mr: 1 }} />
          <span>All</span>
        </div>

        {filteredVendors.length > 0 ? (
          filteredVendors.map((vendor) => (
            <div
              key={vendor}
              onClick={() => handleVendorToggle(vendor)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 8px",
                cursor: "pointer",
                backgroundColor: selectedVendors.includes(vendor)
                  ? isDarkMode
                    ? "rgba(255,255,255,0.05)"
                    : "#F9FAFB"
                  : "transparent",
                borderRadius: "4px",
                marginBottom: "2px",
              }}
            >
              <Checkbox
                size="small"
                checked={selectedVendors.includes(vendor)}
                sx={{ p: 0, mr: 1 }}
              />
              <span
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {vendor}
              </span>
            </div>
          ))
        ) : (
          <div
            style={{ padding: "8px", color: "#9CA3AF", fontStyle: "italic" }}
          >
            No vendors found
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          paddingTop: "8px",
          borderTop: `1px solid ${isDarkMode ? "#2f2f37" : "#E4E7EC"}`,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          fullWidth
          onClick={handleClear}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            borderColor: isDarkMode ? "#2f2f37" : "#D0D5DD",
            color: isDarkMode ? "#E5E7EB" : "#344054",
          }}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          fullWidth
          onClick={handleApply}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            boxShadow: "none",
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );
});

interface SoftwareItem {
  name: string;
  version: string;
  vendor: string;
  occurrences: number;
  cves_count: number;
  cves: string[];
  cpe_string: string;
  status: string;
  asset_ids: string[];
  inactive_asset_ids: string[];
  detected_date?: string;
}

interface ApiResponse {
  total_assets_scanned: number;
  total_software_entries: number;
  results: SoftwareItem[];
}

const caseInsensitiveComparator = (valueA: any, valueB: any) => {
  const strA = valueA ? String(valueA).toLowerCase() : "";
  const strB = valueB ? String(valueB).toLowerCase() : "";
  return strA.localeCompare(strB);
};

const Software: React.FC<{
  fitDrawer?: boolean;
  ip?: string;
  deviceId?: string;
  includeAiSoftware?: boolean;
}> = ({ fitDrawer = false, ip, deviceId, includeAiSoftware = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // State management
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState<any>(null);
  const [softwareData, setSoftwareData] = useState<any[]>([]);
  const [groupedData, setGroupedData] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<"flat" | "grouped">("flat");
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    vendor: [],
  });
  const [searchText, setSearchText] = useState("");

  // Fetch software data from API
  useEffect(() => {
    const fetchSoftwareData = async () => {
      setIsLoading(true);
      try {
        const { getFromLocalStorage } = useStorageManager();
        const userDetails = JSON.parse(
          getFromLocalStorage("userDetails") || "{}",
        );
        const organizationId = userDetails?.organization_id;

        let response;
        if (ip || (deviceId && deviceId.trim() !== "")) {
          const params: any = {};
          if (ip) params.ip = ip;
          if (deviceId && typeof deviceId === "string" && deviceId.trim() !== "") {
            params.device_id = deviceId.trim();
          }
          if (organizationId) params.organization_id = organizationId;
          response = await axiosInstance.get<any>(
            END_POINTS.WMI_SOFTWARE_BY_IP,
            { params },
          );
        } else {
          const params: any = {};
          if (organizationId) params.organization_id = organizationId;

          response = await axiosInstance.get<ApiResponse>(
            END_POINTS.WMI_SOFTWARE_LIST,
            { params },
          );
        }

        if (response.status === 200) {
          const rawGrouped =
            response.data?.grouped_results ||
            response.data?.data?.grouped_results ||
            [];
          const rawResults =
            response.data?.results ||
            response.data?.data?.results ||
            response.data?.software ||
            response.data?.data?.software ||
            (Array.isArray(response.data) ? response.data : []);

          let flattenedResults = [];
          let groupedResults = [];

          if (rawGrouped && rawGrouped.length > 0) {
            groupedResults = rawGrouped;
            flattenedResults = rawGrouped.flatMap(
              (g: any) => g.softwares || [],
            );
          } else {
            flattenedResults = rawResults;
            // 🚀 Dynamic fallback grouping logic
            const groupMap = new Map<string, any[]>();
            rawResults.forEach((item: any) => {
              const groupKey = item.name || item.software_name || "Unknown";
              if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, []);
              }
              groupMap.get(groupKey)!.push(item);
            });
            groupedResults = Array.from(groupMap.entries()).map(
              ([group_name, softwares]) => ({
                group_name,
                software_count: softwares.length,
                total_cves_count: softwares.reduce(
                  (sum: number, s: any) =>
                    sum + (s.cves_count !== undefined ? s.cves_count : s.cves?.length || 0),
                  0,
                ),
                softwares,
              }),
            );
          }

          const parseDetectedDate = (item: any): number => {
            const dateStr =
              item.detected_date ||
              item.raw_detected_date ||
              item.first_detected ||
              item.last_detected ||
              item.installed_on ||
              item.installed_date ||
              item.installation_date ||
              item.created_at ||
              "";
            if (!dateStr || dateStr === "-") return 0;
            const t = new Date(dateStr).getTime();
            return isNaN(t) ? 0 : t;
          };

          let idCounter = 1;
          // Transform API data to match table format
          const transformItem = (item: any) => {
            if (item.cves_count > 0) {
              console.log("Found software with cves_count > 0:", item);
            }
            const rawDetectedDate =
              item.detected_date ||
              item.first_detected ||
              item.last_detected ||
              item.installed_on ||
              item.installed_date ||
              item.installation_date ||
              item.created_at ||
              "";
            return {
              id: item.cpe_string
                ? `${item.cpe_string}_${idCounter++}`
                : idCounter++, // Generate a unique ID for AG Grid
              software_name: item.name || "",
              name: item.name || "",
              version: item.version || "",
              vendor: item.vendor || "",
              occurrences: item.occurrences || 0,
              cves:
                item.cves_count !== undefined
                  ? item.cves_count
                  : item.cves?.length || 0,
              cpe: item.cpe_string || "",
              status: item.status
                ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                : "Active",
              // Pass the lists for the drawer
              cves_list: item.cves || [],
              asset_ids: item.asset_ids || [],
              inactive_asset_ids: item.inactive_asset_ids || [],
              is_ai_related: !!item.is_ai_related,
              raw_detected_date: rawDetectedDate,
              detected_date: rawDetectedDate,
              formatted_detected_date: rawDetectedDate ? (() => {
                const d = new Date(rawDetectedDate);
                return isNaN(d.getTime()) ? rawDetectedDate : d.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                });
              })() : "",
            };
          };

          // Sort flat list descending by detected date (latest date first)
          const rawFlatTransformed = flattenedResults.map((item) => transformItem(item));
          rawFlatTransformed.sort((a, b) => {
            const timeA = parseDetectedDate(a);
            const timeB = parseDetectedDate(b);
            if (timeA !== timeB) return timeB - timeA;
            return caseInsensitiveComparator(a.software_name, b.software_name);
          });

          let globalIndex = 0;
          const transformedData = rawFlatTransformed.map((item) => ({
            ...item,
            serialNumber: ++globalIndex,
          }));
          
          // Process and sort grouped data
          const processedGrouped = groupedResults.map((g: any) => {
            const rawSoftwares = (g.softwares || []).map((item: any) => transformItem(item));
            rawSoftwares.sort((a: any, b: any) => {
              const timeA = parseDetectedDate(a);
              const timeB = parseDetectedDate(b);
              if (timeA !== timeB) return timeB - timeA;
              return caseInsensitiveComparator(a.software_name, b.software_name);
            });

            let maxGroupTime = 0;
            rawSoftwares.forEach((s: any) => {
              const t = parseDetectedDate(s);
              if (t > maxGroupTime) maxGroupTime = t;
            });

            return {
              ...g,
              groupLatestTime: maxGroupTime,
              softwares: rawSoftwares,
            };
          });

          // Sort groups by latest detected date descending (latest first)
          processedGrouped.sort((a: any, b: any) => {
            if (a.groupLatestTime !== b.groupLatestTime) {
              return b.groupLatestTime - a.groupLatestTime;
            }
            return caseInsensitiveComparator(a.group_name, b.group_name);
          });

          let globalGroupIndex = 0;
          let globalIndexForGrouped = 0;

          const transformedGroupedData = processedGrouped.map((g: any) => ({
            ...g,
            serialNumber: ++globalGroupIndex,
            softwares: (g.softwares || []).map((item: any) => ({
              ...item,
              serialNumber: ++globalIndexForGrouped,
            })),
          }));

          setSoftwareData(transformedData);
          setGroupedData(transformedGroupedData);

          const vendors = Array.from(
            new Set(
              transformedData
                .map((item) => item.vendor)
                .filter((v) => v && v.trim() !== ""),
            ),
          ).sort() as string[];
          globalVendors = vendors;
        }
      } catch (error) {
        console.error("Error fetching software data:", error);
        setSoftwareData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSoftwareData();
  }, [ip, deviceId]);

  // 🚀 Listen for custom filter events (matching Asset Management pattern)
  useEffect(() => {
    const handleVendorFilterChange = (event: any) => {
      setActiveFilters((prev) => ({
        ...prev,
        vendor: event.detail.selectedTypes,
      }));
    };

    window.addEventListener("vendorFilterChanged", handleVendorFilterChange);
    return () => {
      window.removeEventListener(
        "vendorFilterChanged",
        handleVendorFilterChange,
      );
    };
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedSoftware(null);
  }, []);

  const handleRowClick = useCallback((data: any) => {
    setSelectedSoftware(data);
    setDrawerOpen(true);
  }, []);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );

  const toggleGroup = useCallback((groupName: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  }, []);

  const displayData = useMemo(() => {
    const searchLower = searchText.trim().toLowerCase();

    const searchableKeys = [
      "serialNumber",
      "software_name",
      "version",
      "vendor",
      "occurrences",
      "cves",
      "cpe",
      "formatted_detected_date"
    ];

    const valueMatches = (row: any, needle: string): boolean => {
        if (!row) return false;
        
        const isGroupRow = row.isGroup || (row.group_name && row.softwares);
        
        if (isGroupRow) {
            const groupName = row.software_name || row.group_name || "";
            const entriesCount = row.entriesCount !== undefined ? row.entriesCount : (row.software_count || row.softwares?.length || 0);
            const totalCves = row.cves !== undefined ? row.cves : (row.total_cves_count !== undefined ? row.total_cves_count : 0);
            const occurrences = row.occurrences !== undefined ? row.occurrences : (row.softwares?.reduce((sum: number, s: any) => sum + (s.occurrences || 0), 0) || 0);

            return (
               (groupName.toString().toLowerCase().includes(needle)) ||
               (entriesCount.toString().toLowerCase().includes(needle)) ||
               (totalCves.toString().toLowerCase().includes(needle)) ||
               (occurrences.toString().toLowerCase().includes(needle)) ||
               (row.serialNumber?.toString().toLowerCase().includes(needle))
            );
        }
        return searchableKeys.some(key => {
            if (key === "serialNumber" && viewMode === "grouped") return false;
            const val = row[key];
            if (val === null || val === undefined) return false;
            return val.toString().toLowerCase().includes(needle);
        });
    };

    // AI-related software is broken out into its own AI Agent Detection panel
    // above the grid (per-device Software tab only, i.e. when ip is set) —
    // excluded here so it isn't shown twice.
    if (viewMode === "flat") {
      // Renumber the "#" column sequentially over the visible rows
      // only — otherwise excluded AI rows leave gaps (e.g. 4, 5, 6).
      const base =
        ip && !includeAiSoftware
          ? softwareData
              .filter((row) => !row.is_ai_related)
              .map((row, i) => ({ ...row, serialNumber: i + 1 }))
          : ip
          ? softwareData.map((row, i) => ({ ...row, serialNumber: i + 1 }))
          : softwareData;
      if (!searchLower) return base;
      return base.filter((row) => valueMatches(row, searchLower));
    }

    const rows: any[] = [];
    let visibleChildSerial = 0;

    for (const group of groupedData) {
      const groupSoftwares =
        ip && !includeAiSoftware
          ? (group.softwares || []).filter((s: any) => !s.is_ai_related)
          : group.softwares || [];
      if (groupSoftwares.length === 0) continue;

      let isGroupMatch = false;
      let matchingChildren = groupSoftwares;

      if (searchLower) {
        isGroupMatch = valueMatches(group, searchLower);
        matchingChildren = groupSoftwares.filter((child: any) =>
          valueMatches(child, searchLower)
        );

        if (!isGroupMatch && matchingChildren.length === 0) {
          continue; // Skip this group if it and its children don't match
        }
      }

      const isExpanded = searchLower ? true : !!expandedGroups[group.group_name];

      const entriesCount = groupSoftwares.length || 0;

      const uniqueVersions = new Set(groupSoftwares.map((s: any) => s.version).filter((v: any) => v && v !== "-"));
      const versionCount = uniqueVersions.size;
      
      const uniqueCpes = new Set(groupSoftwares.map((s: any) => s.cpe).filter((c: any) => c && c !== "-"));
      const cpeCount = uniqueCpes.size;

      let maxDate = "-";
      let latestTime = 0;
      groupSoftwares.forEach((s: any) => {
        if (s.detected_date && s.detected_date !== "-") {
          const time = new Date(s.detected_date).getTime();
          if (!isNaN(time) && time > latestTime) {
            latestTime = time;
            maxDate = s.detected_date;
          }
        }
      });

      rows.push({
        ...group,
        id: `group_${group.group_name}`,
        isGroup: true,
        isExpanded: isExpanded,
        software_name: group.group_name,
        version: entriesCount === 0 ? "-" : (entriesCount === 1 ? "1 version" : `${entriesCount} versions`),
        cpe: entriesCount === 0 ? "-" : (entriesCount === 1 ? "1 CPE" : `${entriesCount} CPEs`),
        detected_date: maxDate,
        entriesCount: entriesCount,
        cves: ip
          ? groupSoftwares.reduce((sum: number, s: any) => sum + (s.cves || 0), 0)
          : group.total_cves_count !== undefined
          ? group.total_cves_count
          : 0,
        occurrences:
          groupSoftwares.reduce(
            (sum: number, s: any) => sum + (s.occurrences || 0),
            0,
          ) || 0,
      });

      if (isExpanded) {
        const childrenToShow = searchLower ? (isGroupMatch && matchingChildren.length === 0 ? groupSoftwares : matchingChildren) : groupSoftwares;
        
        childrenToShow?.forEach((child: any) => {
          rows.push({
            ...child,
            ...(ip ? { serialNumber: ++visibleChildSerial } : null),
            isChild: true,
            parent_group_name: group.group_name,
          });
        });
      }
    }
    return rows;
  }, [viewMode, softwareData, groupedData, expandedGroups, searchText, ip, includeAiSoftware]);

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        field: "number",
        headerName: "#",
        minWidth: 80,
        maxWidth: 100,
        width: 85,
        filter: false,
        sortable: true,
        valueGetter: (params: any) =>
          params.data.isGroup
            ? params.data.serialNumber
            : params.data.isChild
              ? ""
              : params.data.serialNumber,
        comparator: (valueA: any, valueB: any) => {
          const valA = valueA === "" ? NaN : Number(valueA);
          const valB = valueB === "" ? NaN : Number(valueB);
          if (isNaN(valA) && isNaN(valB)) return 0;
          if (isNaN(valA)) return -1;
          if (isNaN(valB)) return 1;
          return valA - valB;
        }
      },
      {
        field: "software_name",
        headerName: "Name",
        filter: false,
        sortable: true,
        comparator: caseInsensitiveComparator,
        minWidth: 260,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.data.isGroup) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  height: "100%",
                  width: "100%",
                }}
              >
                <KeyboardArrowDown
                  sx={{
                    transform: params.data.isExpanded
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: "transform 0.2s",
                    mr: 1,
                    fontSize: "18px",
                    color: isDarkMode ? "#E5E7EB" : "#344054",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "13px",
                    mr: 1.5,
                    color: isDarkMode ? "#FFFFFF" : "#101828",
                  }}
                >
                  {params.value}
                </Typography>
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75, 
                    borderRadius: "999px",
                    backgroundColor: isDarkMode ? "#3B82F6" : "#4A7BF7",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  {params.data.entriesCount}
                </Box>
              </Box>
            );
          }

          const isChild = params.data.isChild;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                pl: isChild ? 4 : 0,
              }}
            >
              <WhiteTooltip
                title={params.value || "-"}
                arrow
                placement="bottom"
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? "#FFFFFF" : "#000000",
                    fontWeight: isChild ? 500 : 700,
                    fontSize: "12px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {params.value || "-"}
                </Typography>
              </WhiteTooltip>
            </Box>
          );
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "version",
        headerName: "Version",
        // width: 130,
        sortable: true,
        comparator: caseInsensitiveComparator,
        filter: false,
        minWidth: 180,
        // maxWidth: 160,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value || "-"} arrow placement="bottom">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {params.value || "-"}
            </Typography>
          </WhiteTooltip>
        ),
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "vendor",
        headerName: "Vendor",
        sortable: true,
        comparator: caseInsensitiveComparator,
        filter: VendorFilter,
        filterParams: {
          isDarkMode: isDarkMode,
        },
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value || "-"} arrow placement="bottom">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {params.value || "-"}
            </Typography>
          </WhiteTooltip>
        ),
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },

      {
        field: "occurrences",
        headerName: "Occurrences",
        sortable: true,
        filter: false,
        // width: 130,
        minWidth: 110,
        // maxWidth: 160,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value || "-"} arrow placement="bottom">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {params.value}
            </Typography>
          </WhiteTooltip>
        ),
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "cves",
        headerName: "CVE's",
        sortable: true,
        filter: false,
        // width: 110,
        minWidth: 45,
        // maxWidth: 140,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip
            title={`CVEs: ${params.value || 0}`}
            arrow
            placement="bottom"
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <img
                src={params.value > 0 ? cveBugError : cveBug}
                alt="CVE"
                style={{ width: "20px", height: "20px" }}
              />
              {params.value}
            </Typography>
          </WhiteTooltip>
        ),
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "cpe",
        headerName: "CPE",
        hide: true,
        sortable: true,
        comparator: caseInsensitiveComparator,
        filter: false,
        // flex: 1,
        minWidth: 350,
        cellRenderer: (params: ICellRendererParams) => (
          <WhiteTooltip title={params.value || "-"} arrow placement="bottom">
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                fontFamily: "monospace",
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                minWidth: 0,
              }}
            >
              {params.value || "-"}
            </Typography>
          </WhiteTooltip>
        ),
        cellStyle: {
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        },
      },
      {
        field: "detected_date",
        headerName: "Detected Date",
        sortable: true,
        filter: false,
        minWidth: 160,
        comparator: (dateA: any, dateB: any) => {
          const timeA = dateA && dateA !== "-" ? new Date(dateA).getTime() : 0;
          const timeB = dateB && dateB !== "-" ? new Date(dateB).getTime() : 0;
          const validA = isNaN(timeA) ? 0 : timeA;
          const validB = isNaN(timeB) ? 0 : timeB;
          return validA - validB;
        },
        cellRenderer: (params: ICellRendererParams) => {
          const dateStr = params.value;
          if (!dateStr) return <span>-</span>;
          const date = new Date(dateStr);
          const formattedDate = isNaN(date.getTime())
            ? dateStr
            : date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
          return (
            <WhiteTooltip title={dateStr} arrow placement="bottom">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {formattedDate}
              </Typography>
            </WhiteTooltip>
          );
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
      },
    ];

    if (ip) {
      return cols.filter(
        (col) => col.field !== "occurrences" && col.field !== "cves",
      );
    }
    return cols;
  }, [ip, isDarkMode]);

  const defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
  };

  const gridOptions: GridOptions = {
    defaultColDef,
    rowHeight: 48,
    headerHeight: 48,
    suppressCellFocus: true,
    rowStyle: { cursor: ip ? "default" : "pointer" },
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    getRowId: (params) => params.data.id,
    postSortRows: (params: any) => {
      const rowNodes = params.nodes;
      const sortedGroups: any[] = [];
      const childMap = new Map<string, any[]>();
      let isGroupedMode = false;

      // First pass: separate groups and children
      rowNodes.forEach((node: any) => {
        if (node.data?.isGroup) {
          sortedGroups.push(node);
          isGroupedMode = true;
        } else if (node.data?.isChild) {
          const parentName = node.data.parent_group_name;
          if (!childMap.has(parentName)) {
            childMap.set(parentName, []);
          }
          childMap.get(parentName)!.push(node);
          isGroupedMode = true;
        }
      });

      if (!isGroupedMode) return;

      // Second pass: rebuild the rowNodes array in-place
      let nextInsertPos = 0;
      sortedGroups.forEach((groupNode) => {
        rowNodes[nextInsertPos++] = groupNode;
        const children = childMap.get(groupNode.data.group_name);
        if (children) {
          children.forEach((childNode) => {
            rowNodes[nextInsertPos++] = childNode;
          });
        }
      });

      // Just in case there are orphan children (shouldn't happen)
      childMap.forEach((children, parentName) => {
        if (!sortedGroups.find((g) => g.data.group_name === parentName)) {
          children.forEach((childNode) => {
            rowNodes[nextInsertPos++] = childNode;
          });
        }
      });
    },
    rowClassRules: {
      "ag-row-group": (params) => params.data?.isGroup === true,
      "ag-row-child": (params) => params.data?.isChild === true,
    },
    onCellClicked: ip
      ? undefined
      : (params: any) => {
          const data = params.data;
          if (data.isGroup) {
            toggleGroup(data.group_name);
            return;
          }
          if (params.colDef?.field === "cves") {
            setSelectedSoftware({ ...data, openToTab: 1 });
          } else {
            setSelectedSoftware({ ...data, openToTab: 0 });
          }
          setDrawerOpen(true);
        },
  };

  // 🚀 Grouped view toggle headerActions
  const headerActions = useMemo(() => {
    if (ip) return undefined;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mr: 1,
        }}
      >
        <Typography
          onClick={() => {
            setViewMode("flat");
            setExpandedGroups({});
          }}
          sx={{
            fontSize: "13px",
            fontWeight: viewMode === "flat" ? 700 : 500,
            color:
              viewMode === "flat"
                ? isDarkMode
                  ? "#FFFFFF"
                  : "#101828"
                : isDarkMode
                ? "#9CA3AF"
                : "#667085",
            transition: "all 0.2s ease-in-out",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          Flat List
        </Typography>
        <FormControlLabel
          control={
            <IOSSwitch
              checked={viewMode === "grouped"}
              onChange={(e) => {
                setViewMode(e.target.checked ? "grouped" : "flat");
                setExpandedGroups({});
              }}
              width={38}
              height={20}
              thumbSize={16}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                ml: 0.5,
                fontSize: "13px",
                fontWeight: viewMode === "grouped" ? 700 : 500,
                color:
                  viewMode === "grouped"
                    ? isDarkMode
                      ? "#FFFFFF"
                      : "#101828"
                    : isDarkMode
                    ? "#9CA3AF"
                    : "#667085",
                transition: "all 0.2s ease-in-out",
                userSelect: "none",
              }}
            >
              Grouped
            </Typography>
          }
          labelPlacement="end"
          sx={{ m: 0 }}
        />
      </Box>
    );
  }, [ip, viewMode, isDarkMode]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          height: fitDrawer ? "100%" : "calc(100vh - 120px)",
          display: fitDrawer ? "flex" : undefined,
          flexDirection: fitDrawer ? "column" : undefined,
          px: fitDrawer ? 0 : undefined,
          py: fitDrawer ? 0 : undefined,
          borderRadius: 3,
          backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
        className="white-box"
      >
        <AgGridTable
          columnDefs={columnDefs}
          data={displayData}
          activeFilters={activeFilters}
          height={fitDrawer ? "100%" : `calc(100vh - 120px)`}
          showSearch
          searchPlaceholder="Search Software"
          headerActions={headerActions}
          onSearchChange={setSearchText}
          disableLocalSearch={true}
          pagination={true}
          paginationPageSize={50}
          isLoading={isLoading}
          gridOptions={gridOptions}
          styles={{
            "& .ag-theme-material .ag-row.ag-row-group": {
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.02) !important"
                : "#F9FAFB !important",
              borderBottom: `1px solid ${isDarkMode ? "#2f2f37" : "#EAECF0"}`,
            },
          }}
        />
      </Card>

      {/* Software Details Drawer */}
      <SoftwareDetailsDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        softwareData={selectedSoftware}
        renderSoftwarePage={(targetIp, targetDeviceId) => (
          <Software fitDrawer ip={targetIp} deviceId={targetDeviceId} includeAiSoftware={true} />
        )}
      />
    </>
  );
};

export default Software;
